import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button } from 'react-bootstrap';
import GeminiService from '../services/GeminiService';
import StorageService from '../services/StorageService';
import VoiceService from '../services/VoiceService';
import { UserIcon, BotIcon, MicIcon } from '../assets/icons';
import '../styles/ChatInterface.css';

const ChatInterface = ({ settings }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);
  const geminiService = useRef(null);
  const voiceService = useRef(null);
  const isAdmin = process.env.REACT_APP_IS_ADMIN === 'true';

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    geminiService.current = new GeminiService(apiKey);
    voiceService.current = new VoiceService();
    
    const savedMessages = StorageService.getMessages();
    if (savedMessages.length > 0) {
      setMessages(savedMessages);
    }
  }, []);

  useEffect(() => {
    StorageService.saveMessages(messages);
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const extractSourceFromResponse = (response) => {
    const sourceMatch = response.match(/---\n\*Source: ([^*]+)\*/);
    if (sourceMatch) {
      const source = sourceMatch[1].trim();
      if (isAdmin) {
        console.log('Response Source:', source);
      }
      return response.replace(/---\n\*Source: [^*]+\*/, '').trim();
    }
    return response;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await geminiService.current.generateResponse(inputMessage, settings);
      const cleanResponse = extractSourceFromResponse(response);
      
      const botMessage = {
        text: cleanResponse,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage = {
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleVoiceInput = () => {
    if (isListening) {
      voiceService.current.stopListening();
      setIsListening(false);
    } else {
      setIsListening(true);
      voiceService.current.startListening(
        (transcript) => {
          setInputMessage(transcript);
        },
        () => {
          setIsListening(false);
          if (inputMessage.trim()) {
            handleSubmit();
          }
        },
        (error) => {
          console.error('Voice input error:', error);
          setIsListening(false);
        }
      );
    }
  };

  const clearChat = () => {
    setMessages([]);
    StorageService.saveMessages([]);
  };

  return (
    <Container className="chat-container">

      <div className="chat-messages">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`message ${message.sender} ${message.isError ? 'error' : ''}`}
          >
            <div className="message-icon">
              {message.sender === 'user' ? <UserIcon /> : <BotIcon />}
            </div>
            <div className="message-content">
              {message.text.split('\n').map((line, i) => (
                line.trim() && <p key={i}>{line}</p>
              ))}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message bot">
            <div className="message-icon">
              <BotIcon />
            </div>
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">

      <Form onSubmit={handleSubmit}>
        <Form.Group className="d-flex">
            <Form.Control
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading || isListening}
            />
          {voiceService.current?.isVoiceSupported() && (
            <Button
              type="button"
              variant={isListening ? "danger" : "outline-secondary"}
              onClick={toggleVoiceInput}
              className="mic-button"
              disabled={isLoading}
            >
              <MicIcon isListening={isListening} />
            </Button>
          )}

          <Button 
            type="submit" 
            variant="primary" 
            disabled={isLoading || !inputMessage.trim()}
          >
            Send
          </Button>

          <Button 
            variant="secondary" 
            onClick={clearChat}
            className="ms-2"
          >
            Clear
          </Button>
          
        </Form.Group>
      </Form>
      </div>
    </Container>
  );
};

export default ChatInterface;
